import { useDeviceAlertConfiguration } from '@/api/customSensorAlerts';
import { SensorAlertTypes } from '@/api/customSensorAlerts/types';
import { AlertChip } from '@/components/AlertChip';
import { Datum, DatumContext, DatumUnit } from '@/components/data/Datum';
import {
    AlertComparisonOperators,
    BreachedSensorAlert,
    applicableDeviceAlerts,
    composeMostSevereBreachedAlerts,
    convertAlertThreshold,
    isAlertThresholdBreached,
    prettyOperator,
} from '@/utils';

export const SensorAlert = ({
    isSmartMooring,
    sensorPosition,
    spotterId,
    alertType,
    currentValue,
    currentValueUnit,
    context = {},
}: {
    isSmartMooring?: boolean;
    sensorPosition?: number;
    spotterId?: string;
    alertType: SensorAlertTypes;
    currentValue: number;
    currentValueUnit: DatumUnit;
    context?: DatumContext;
}) => {
    const { data: deviceAlerts } = useDeviceAlertConfiguration({
        spotterId,
    });

    if (!spotterId) {
        // There would be no spotterId in the map inspector for public spotters.
        return null;
    }

    const breachedAlerts: BreachedSensorAlert[] = applicableDeviceAlerts(
        deviceAlerts?.data ?? [],
        alertType,
        isSmartMooring ? sensorPosition : undefined
    )
        .map((alert) => {
            const { convertedValue, unitType } = convertAlertThreshold(alert, currentValueUnit, context);
            return {
                id: alert.id,
                type: alert.trigger_details.trigger_type as SensorAlertTypes,
                logicalOperator: alert.trigger_details.operator as AlertComparisonOperators,
                convertedAlertValue: convertedValue,
                convertedAlertUnits: currentValueUnit,
                unitType,
                displayName: alert.trigger_details.displayName.toLowerCase(),
            };
        })
        .filter((alert) => isAlertThresholdBreached(currentValue, alert.logicalOperator, alert.convertedAlertValue));

    // Alerts are already of the same type here, so we just need to find the most severe breaches per operator
    const alerts = composeMostSevereBreachedAlerts(breachedAlerts);

    return (
        <>
            {alerts?.map((alert) => (
                <AlertChip
                    key={alert.id}
                    severity="warning"
                    label={
                        <>
                            {prettyOperator(alert.logicalOperator)}
                            <Datum
                                value={alert.convertedAlertValue}
                                sourceUnit={alert.convertedAlertUnits}
                                type={alert.unitType}
                                maxDecimalPlaces={1}
                                variant="inherit"
                                context={context}
                            />{' '}
                            {alert.displayName}
                        </>
                    }
                />
            ))}
        </>
    );
};
